/**
 * Drawer persistence — the seam between the drawer store and where its
 * open/closed state is remembered. The desktop drawer persists to
 * localStorage; the mobile drawer doesn't persist at all; tests supply a
 * fake. One store definition (`createDrawerStore`) serves all three.
 */
export interface DrawerPersistence {
  /** Initial open state when the store is created. */
  load: () => boolean;
  /** Persist a new open state. */
  save: (open: boolean) => void;
}

/** Persists the drawer's open state to `localStorage` under `key` (JSON boolean). */
export function localStoragePersistence(key: string, fallback = true): DrawerPersistence {
  return {
    load: () => {
      try {
        const raw = localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw) === true;
      } catch {
        return fallback;
      }
    },
    save: (open) => {
      try {
        localStorage.setItem(key, JSON.stringify(open));
      } catch {
        /* quota exceeded or storage disabled — nothing to persist to */
      }
    },
  };
}

/** No persistence: always starts from `initial` (default closed), never saves. */
export function ephemeralPersistence(initial = false): DrawerPersistence {
  return { load: () => initial, save: () => {} };
}
